import { RefObject } from 'react'

import { AllCurrenciesT, CryptoCurrenciesE, FiatCurrenciesE } from 'primsell-shared-layer/types'

export const isEllipsisActive = (ref: RefObject<HTMLElement> | null): boolean => {
  return !!ref?.current && ref.current.offsetWidth < ref.current.scrollWidth
}

export const debounce = <T extends (...args: any[]) => void>(fn: T, ms = 300): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout>
  return function debounceClosure(this: any, ...args: any[]) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), ms)
  }
}

export const hasMatchingParent = (element: HTMLElement | null, ids: string[]): boolean => {
  while (element) {
    if (ids.includes(element.id)) {
      return true
    }
    element = element.parentNode as HTMLElement
  }
  return false
}

export function shortCutWalletAddress(wallet: string): string {
  if (wallet && wallet?.length < 20) {
    return wallet
  }
  return `${wallet?.substring(0, 6)}...${wallet?.substring(wallet.length - 4)}`
}

export function shortCutTransactionHash(tx: string): string {
  return `${tx?.substring(0, 4)}...${tx?.substring(tx.length - 4)}`
}

export enum EthUnit {
  Wei,
  Gwei,
  Ether,
}

export function ethConverter(value: bigint, from: EthUnit, to: EthUnit): bigint {
  // Conversion factors based on the Ethereum denominations.
  const WEI_IN_GWEI = BigInt(1000000000) // 1 Gwei = 1,000,000,000 Wei
  const WEI_IN_ETHER = BigInt('1000000000000000000') // 1 Ether = 1,000,000,000,000,000,000 Wei

  let valueInWei: bigint

  // Convert the input value to Wei (base unit)
  switch (from) {
    case EthUnit.Wei:
      valueInWei = value
      break
    case EthUnit.Gwei:
      valueInWei = value * WEI_IN_GWEI
      break
    case EthUnit.Ether:
      valueInWei = value * WEI_IN_ETHER
      break
  }

  // Convert the value in Wei to the desired target unit
  switch (to) {
    case EthUnit.Wei:
      return valueInWei
    case EthUnit.Gwei:
      return valueInWei / WEI_IN_GWEI
    case EthUnit.Ether:
      return valueInWei / WEI_IN_ETHER
  }

  throw new Error('Invalid conversion units provided.')
}

const currencyToDecimalPlaces: Record<AllCurrenciesT, number> = {
  [CryptoCurrenciesE.MATIC]: 2,
  [CryptoCurrenciesE.WETH]: 4,
  [FiatCurrenciesE.EUR]: 2,
  [FiatCurrenciesE.CAD]: 2,
}

export const getFormattedAmount = (amount?: number, currency?: AllCurrenciesT | null, roundUp?: boolean): string => {
  if (!amount || !currency) return '0'

  const decimalPlaces = currencyToDecimalPlaces[currency]
  const minNumber = 1 / 10 ** decimalPlaces
  const moduleAmount = Math.abs(amount)

  if (roundUp && moduleAmount < minNumber) {
    return minNumber.toString()
  }

  const numberString = moduleAmount.toString()

  if (numberString.includes('.')) {
    const [integerPart, decimalPart] = numberString.split('.')

    let decimalResult = decimalPart

    if (decimalPart.length > decimalPlaces) {
      decimalResult = decimalPart.slice(0, decimalPlaces)
    } else {
      while (decimalResult.length < decimalPlaces) {
        decimalResult += '0'
      }
    }

    return `${integerPart}.${decimalResult}`
  } else {
    return `${numberString}.${'0'.repeat(currencyToDecimalPlaces[currency])}`
  }
}

export const getRoundedAmount = (value: string | number | null, currency: AllCurrenciesT) => {
  if (!value) {
    return '0'
  }

  const numberValue = Number(value)

  const multiplicationAmount = Math.pow(10, currencyToDecimalPlaces[currency])
  const computedValue = Math.round(numberValue * multiplicationAmount) / multiplicationAmount

  const stringifiedValue = computedValue.toString()

  if (stringifiedValue.includes('.')) {
    const decimalPartLength = stringifiedValue.split('.')[1].length

    if (decimalPartLength < currencyToDecimalPlaces[currency]) {
      return `${stringifiedValue}${'0'.repeat(currencyToDecimalPlaces[currency] - decimalPartLength)}`
    }

    return stringifiedValue.slice(0, stringifiedValue.length - (decimalPartLength - currencyToDecimalPlaces[currency]))
  }

  return `${stringifiedValue}.${'0'.repeat(currencyToDecimalPlaces[currency])}`
}
